// UserProfile.js - Enhanced with Unified Feed & Improved Bubble Cards
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  CssBaseline, Backdrop, CircularProgress, Avatar, Box, Typography, 
  Paper, Grid, Button, Tab, Tabs, Chip, Alert, AlertTitle, 
  IconButton, Tooltip, Card, CardContent, CardActions, Stack, 
  Container, Fade, Zoom, useMediaQuery, Skeleton,
  ClickAwayListener, Popper, Grow, MenuList, MenuItem,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import PlaceIcon from "@mui/icons-material/Place";
import WorkIcon from "@mui/icons-material/Work";
import CampaignIcon from "@mui/icons-material/Campaign";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ChatIcon from "@mui/icons-material/Chat";
import LockIcon from "@mui/icons-material/Lock";
import PostAddIcon from "@mui/icons-material/PostAdd";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import ShareIcon from "@mui/icons-material/Share";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VerifiedIcon from "@mui/icons-material/Verified";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublicIcon from "@mui/icons-material/Public";
import PeopleIcon from "@mui/icons-material/People";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import API from "../../../API";
import ChatModal from "./chatmodal";
import ChatModalCreator from "../../../Components/ChatComponents/creatorchatmodal";
import PageTitle from "../../../Components/PageTitle";
import useAlertDialog from "../../../Components/useAlertDialog";
import { replaceConsoleError } from '../../../Utils/toastNotifications';
import AlertDialog from "../../../Components/AlertDialog";
import useAuth from "../../../Hooks/use-auth";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import { toast } from 'sonner';
import routes from "../../../Config/routes";
import T2AUniversalDialog from "../../../GlobalPages/globalUser/UniversalT2A";

/* ────────────────────────────────────────────────────────────────
  Transfer Button Styled Components
──────────────────────────────────────────────────────────────── */
const TransferButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
  color: '#FFFFFF',
  borderRadius: '999px',
  height: '52px',
  padding: '0 22px 0 26px',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  boxShadow: '0 10px 24px rgba(124, 58, 237, 0.28)',
  transition: 'all 0.22s ease',
  gap: '6px',
  '&:hover': {
    background: 'linear-gradient(135deg, #6d28d9 0%, #4338ca 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 30px rgba(124, 58, 237, 0.38)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const TransferMenu = styled(Paper)(({ theme }) => ({
  borderRadius: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(124,58,237,0.22)' : 'rgba(124,58,237,0.16)'}`,
  boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(124,58,237,0.12)',
  overflow: 'hidden',
  minWidth: '230px',
  backdropFilter: 'blur(16px)',
  background: theme.palette.mode === 'dark'
    ? 'rgba(20,20,30,0.97)'
    : 'rgba(255,255,255,0.98)',
}));

const TransferMenuItem = styled(MenuItem)(({ theme, itemcolor }) => ({
  padding: '10px 14px',
  margin: '2px 8px',
  borderRadius: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  fontWeight: 600,
  fontSize: '0.95rem',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
  transition: 'all 0.18s ease',
  '&:hover': {
    backgroundColor: itemcolor
      ? alpha(itemcolor, 0.1)
      : alpha(theme.palette.primary.main, 0.08),
    color: itemcolor || theme.palette.primary.main,
    '& .transfer-icon-wrap': {
      transform: 'scale(1.12)',
    },
  },
  '& .transfer-icon-wrap': {
    width: 38,
    height: 38,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: itemcolor ? alpha(itemcolor, 0.12) : alpha(theme.palette.primary.main, 0.1),
    color: itemcolor || theme.palette.primary.main,
    transition: 'transform 0.18s ease',
    flexShrink: 0,
  },
}));

/* ────────────────────────────────────────────────────────────────
  TransferDropdownButton component
──────────────────────────────────────────────────────────────── */
function TransferDropdownButton({ onGift, onRequest }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <>
      <TransferButton
        ref={anchorRef}
        onClick={handleToggle}
        startIcon={<SwapHorizIcon sx={{ fontSize: '1.2rem' }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '1.15rem',
              transition: 'transform 0.22s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
      >
        Transfer
      </TransferButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        style={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'top left' }}
            timeout={180}
          >
            <TransferMenu elevation={0}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList disablePadding sx={{ py: 1.5 }}>
                  <Box sx={{ px: 3, pt: 0.5, pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', fontSize: '0.68rem' }}>
                      Choose action
                    </Typography>
                  </Box>

                  <TransferMenuItem
                    itemcolor="#7c3aed"
                    onClick={() => { setOpen(false); onGift(); }}
                  >
                    <Box className="transfer-icon-wrap">
                      <CardGiftcardIcon sx={{ fontSize: '1.1rem' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} lineHeight={1.2}>Send Balance</Typography>
                      <Typography variant="caption" color="text.secondary" lineHeight={1.4}>Gift funds instantly</Typography>
                    </Box>
                  </TransferMenuItem>

                  <TransferMenuItem
                    itemcolor="#10b981"
                    onClick={() => { setOpen(false); onRequest(); }}
                  >
                    <Box className="transfer-icon-wrap" sx={{ background: `${alpha('#10b981', 0.12)} !important`, color: '#10b981 !important' }}>
                      <RequestPageIcon sx={{ fontSize: '1.1rem' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} lineHeight={1.2}>Request Balance</Typography>
                      <Typography variant="caption" color="text.secondary" lineHeight={1.4}>Ask for payment</Typography>
                    </Box>
                  </TransferMenuItem>
                </MenuList>
              </ClickAwayListener>
            </TransferMenu>
          </Grow>
        )}
      </Popper>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────
  Helpers
──────────────────────────────────────────────────────────────── */
const iconForProject = (type) => {
  switch (type) {
    case "campaign":
    case "partnership":
      return <BusinessCenterIcon />;
    case "payout":
      return <AttachMoneyIcon />;
    case "Post":
      return <PostAddIcon />;
    default:
      return <CampaignIcon />;
  }
};

const getReactionIcon = (type) => {
  switch (type) {
    case "like":
      return <ThumbUpIcon fontSize="small" />;
    case "love":
      return <FavoriteIcon fontSize="small" />;
    case "haha":
      return <EmojiEmotionsIcon fontSize="small" />;
    default:
      return <ThumbUpIcon fontSize="small" />;
  }
};

const getProjectColors = (type, isDark) => {
  const colors = {
    campaign: {
      light: { primary: "#6366F1", bg: "rgba(99, 102, 241, 0.1)", hover: "rgba(99, 102, 241, 0.2)" },
      dark: { primary: "#818CF8", bg: "rgba(129, 140, 248, 0.1)", hover: "rgba(129, 140, 248, 0.2)" }
    },
    partnership: {
      light: { primary: "#10B981", bg: "rgba(16, 185, 129, 0.1)", hover: "rgba(16, 185, 129, 0.2)" },
      dark: { primary: "#34D399", bg: "rgba(52, 211, 153, 0.1)", hover: "rgba(52, 211, 153, 0.2)" }
    },
    payout: {
      light: { primary: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)", hover: "rgba(245, 158, 11, 0.2)" },
      dark: { primary: "#FBBF24", bg: "rgba(251, 191, 36, 0.1)", hover: "rgba(251, 191, 36, 0.2)" }
    },
    Post: {
      light: { primary: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)", hover: "rgba(139, 92, 246, 0.2)" },
      dark: { primary: "#A78BFA", bg: "rgba(167, 139, 250, 0.1)", hover: "rgba(167, 139, 250, 0.2)" }
    }
  };
  return colors[type]?.[isDark ? 'dark' : 'light'] || colors.campaign[isDark ? 'dark' : 'light'];
};

const normalizeVisibilityValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "public"].includes(normalized)) return true;
    if (["false", "0", "no", "private"].includes(normalized)) return false;
  }
  return false;
};

const resolveBlitzboltsPublicSetting = (profile) => {
  if (!profile || typeof profile !== "object") return false;
  const rawValue =
    profile.blitzbolts_public ??
    profile.blitzboltsPublic ??
    profile.blitz_bolts_public ??
    profile.blitz_bolt_public ??
    profile.blitz_bold_public ??
    profile.user_settings?.blitzbolts_public ??
    profile.user_settings?.blitzboltsPublic;
  return normalizeVisibilityValue(rawValue);
};

/* ────────────────────────────────────────────────────────────────
  Component
──────────────────────────────────────────────────────────────── */
export default function UserProfile({ 
  initialUserData = null, 
  userIdFromParams = null 
}) {
  const { userId: paramUserId } = useParams();
  const userId = userIdFromParams || paramUserId;
  
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { dialogState, openDialog } = useAlertDialog();
  
  // DUAL AUTH SUPPORT
  const { isAuthorized: isUserAuthorized, getCurrrentUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, user: creatorUser, isInitialized: isCreatorInitialized } = useCreatorAuth();
  
  const currentUser = getCurrrentUser?.();
  const isOwnUserProfile = Boolean(
    currentUser?.id !== undefined &&
      userId !== undefined &&
      String(currentUser.id) === String(userId)
  );

  const isAuthorized = isUserAuthorized || isCreatorAuthorized;
  const isCreatorMode = isCreatorAuthorized && !isUserAuthorized;

  const tabsRef = useRef(null);

  // State
  const [user, setUser] = useState(initialUserData);
  const [loading, setLoading] = useState(!initialUserData);
  const [tab, setTab] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [blitzBolts, setBlitzBolts] = useState([]);
  const [blitzBoltsLoading, setBlitzBoltsLoading] = useState(false);
  const [userBubbles, setUserBubbles] = useState([]);
  const [bubblesLoading, setBubblesLoading] = useState(false);

  // ── T2A dialog state ──────────────────────────────────────────────────────
  const [t2aOpen, setT2aOpen] = useState(false);
  const [t2aMode, setT2aMode] = useState("gift");

  const resolveRecipientGUId = null;

  const canViewAchievements = useMemo(() => {
    if (isOwnUserProfile) return true;
    return resolveBlitzboltsPublicSetting(user);
  }, [isOwnUserProfile, user]);

  /* ───── Fetch user data ───── */
  useEffect(() => {
    if (initialUserData || !userId) {
      setLoading(false);
      return;
    }
    let mounted = true;
    const fetchUser = async () => {
      try {
        setLoading(true);
        const res = await API.users.fetchPublicDetails(userId);
        if (mounted) setUser(res);
      } catch (err) {
        if (mounted) openDialog("Error", err.message || "Failed to load user profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchUser();
    return () => { mounted = false; };
  }, [userId, openDialog, initialUserData]);

  /* ───── fetch user posts ───── */
  useEffect(() => {
    if (!userId || !isAuthorized) return;
    let mounted = true;
    (async () => {
      setPostsLoading(true);
      try {
        const res = await API.zapfeed.getUserPosts(userId, { limit: 100 });
        if (mounted) setUserPosts(res?.posts ?? res ?? []);
      } catch (err) {
        replaceConsoleError("Failed to fetch user posts:", err);
      } finally {
        if (mounted) setPostsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId, isAuthorized]);

  /* ───── fetch user blitz bolts ───── */
  useEffect(() => {
    if (!userId || !canViewAchievements) {
      setBlitzBolts([]);
      setBlitzBoltsLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      setBlitzBoltsLoading(true);
      try {
        const res = await API.blitzbolts.listForUser(userId, {
          scope: isOwnUserProfile ? 'self' : 'public',
        });
        if (mounted) setBlitzBolts(Array.isArray(res) ? res : []);
      } catch (err) {
        replaceConsoleError("Failed to fetch user blitz bolts:", err);
        if (mounted) setBlitzBolts([]);
      } finally {
        if (mounted) setBlitzBoltsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId, isOwnUserProfile, canViewAchievements]);

  /* ───── fetch user bubbles ───── */
  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    (async () => {
      setBubblesLoading(true);
      try {
        const res = await API.bubbles.getUserSummary(userId);
        if (mounted) setUserBubbles(res?.bubbles || []);
      } catch (err) {
        replaceConsoleError("Failed to fetch user bubbles:", err);
        if (mounted) setUserBubbles([]);
      } finally {
        if (mounted) setBubblesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  /* ───── derived data ───── */
  const {
    first_name: first = "",
    last_name: last = "",
    company = {},
    campaigns = [],
    partnerships = [],
    payouts = [],
    created_at,
  } = user || {};

  const activePartnerships = useMemo(
    () => partnerships.filter((p) => p.status?.toUpperCase() !== "DELETED"),
    [partnerships]
  );

  const achievedBlitzBolts = useMemo(
    () => blitzBolts.filter(bolt => bolt.attainment?.achieved === true),
    [blitzBolts]
  );

  const totalProjects = campaigns.length + activePartnerships.length;

  const refetchUserPosts = React.useCallback(async () => {
    if (!userId || !isAuthorized) return;
    setPostsLoading(true);
    try {
      const res = await API.zapfeed.getUserPosts(userId, { limit: 100 });
      setUserPosts(res?.posts ?? res ?? []);
    } catch (err) {
      replaceConsoleError("Failed to refetch user posts:", err);
    } finally {
      setPostsLoading(false);
    }
  }, [userId, isAuthorized]);

  const pickDate = (obj) =>
    new Date(obj.activity_date || obj.proposal_date || obj.payout_date || obj.created_at || obj.updated_at || 0);

  const postsSorted = useMemo(() => {
    const list = Array.isArray(userPosts) ? userPosts : [];
    return [...list].sort((a, b) => pickDate(b) - pickDate(a));
  }, [userPosts]);

  const projectsSorted = useMemo(() => {
    const mappedCampaigns = campaigns.map(c => ({ ...c, activity_type: "campaign", activity_date: c.activity_date || c.proposal_date || c.created_at }));
    const mappedPartnerships = activePartnerships.map(p => ({ ...p, activity_type: "partnership", activity_date: p.activity_date || p.proposal_date || p.created_at }));
    const mappedPayouts = payouts.map(p => ({ ...p, activity_type: "payout", activity_date: p.activity_date || p.payout_date || p.created_at, name: p.name || `Payout #${p.id}` }));
    return [...mappedCampaigns, ...mappedPartnerships, ...mappedPayouts].sort((a, b) => pickDate(b) - pickDate(a));
  }, [campaigns, activePartnerships, payouts]);

  const unifiedFeed = useMemo(() => {
    const bubbleItems = userBubbles.map(bubble => ({ ...bubble, activity_type: "bubble", activity_date: bubble.created_at, isPinned: true }));
    const otherActivities = [...postsSorted, ...projectsSorted].sort((a, b) => pickDate(b) - pickDate(a));
    return [...bubbleItems, ...otherActivities];
  }, [userBubbles, postsSorted, projectsSorted]);

  /* ───── handlers ───── */
  const handleTabChange = (_, newValue) => {
    setTab(newValue);
    if (tabsRef.current) {
      setTimeout(() => {
        const y = tabsRef.current.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    if (!isAuthorized) return;
    try {
      await API.zapfeed.addReaction(postId, reactionType);
      await refetchUserPosts();
    } catch (err) {
      replaceConsoleError("Failed to add reaction:", err);
    }
  };

  const handleShareBlitzBolt = (bolt) => {
    if (bolt.attainment?.id) {
      const shareUrl = API.blitzbolts.shareUrl(userId, bolt.attainment.id);
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share URL copied to clipboard', { description: 'Link ready to share' });
    }
  };

  const handleChatClick = () => {
    if (!isAuthorized) {
      openDialog("Authentication Required", "You must be logged in to start a chat conversation.");
      return;
    }
    setChatOpen(true);
  };

  const handleViewBubble = (bubbleId) => {
    navigate(routes.bubblePublic.replace(':bubbleId', bubbleId));
  };

  const handleOpenBubbleChat = (bubble) => {
    if (bubble.has_chat && bubble.conversation_id) {
      navigate(`${routes.conversationshub}?conversation=${bubble.conversation_id}`);
    } else {
      toast.info("This bubble doesn't have a chat yet");
    }
  };

  /* ───── ui state ───── */
  if (loading || (isCreatorMode && !isCreatorInitialized)) {
    return (
      <Backdrop open sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">User not found</Typography>
      </Container>
    );
  }

  /* ───── render helpers ───── */
  const EnhancedBubbleCard = ({ bubble, index }) => (
    <Zoom in timeout={300 + index * 100}>
      <Card elevation={0} sx={{
        borderRadius: 3,
        border: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`,
        backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer', position: 'relative', overflow: 'hidden',
        height: '100%', display: 'flex', flexDirection: 'column',
        '&:hover': { transform: 'translateY(-6px)', boxShadow: isDarkMode ? '0 25px 50px rgba(0,0,0,0.4)' : '0 25px 50px rgba(0,0,0,0.15)', borderColor: bubble.theme_colors?.primary || theme.palette.primary.main },
      }}>
        <Box onClick={() => handleViewBubble(bubble.id)} sx={{
          height: { xs: 120, sm: 140 }, width: '100%', position: 'relative', overflow: 'hidden',
          background: bubble.cover_image ? `url(${bubble.cover_image}) center/cover` : bubble.theme_colors?.primary && bubble.theme_colors?.secondary ? `linear-gradient(135deg, ${bubble.theme_colors.primary} 0%, ${bubble.theme_colors.secondary} 100%)` : `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          '&::after': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)' }
        }}>
          {bubble.cover_image && bubble.theme_colors?.primary && bubble.theme_colors?.secondary && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `linear-gradient(135deg, ${alpha(bubble.theme_colors.primary, 0.3)} 0%, ${alpha(bubble.theme_colors.secondary, 0.3)} 100%)`, mixBlendMode: 'overlay' }} />
          )}
          <Avatar src={bubble.avatar_image} sx={{ width: { xs: 70, sm: 80 }, height: { xs: 70, sm: 80 }, position: 'absolute', bottom: -35, left: { xs: 16, sm: 24 }, border: `4px solid ${isDarkMode ? '#1A1A1A' : '#FFFFFF'}`, bgcolor: bubble.theme_colors?.primary || theme.palette.primary.main, zIndex: 2, boxShadow: '0 8px 20px rgba(0,0,0,0.3)' }}>
            {!bubble.avatar_image && <GroupsIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: bubble.theme_colors?.secondary || '#FFFFFF' }} />}
          </Avatar>
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
            {bubble.is_public ? <PublicIcon sx={{ fontSize: 16, color: '#FFFFFF' }} /> : <LockIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />}
            <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600 }}>{bubble.is_public ? 'Public' : 'Private'}</Typography>
          </Box>
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 5, sm: 6 }, flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bubble.name}</Typography>
          {bubble.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.6, minHeight: 40 }}>{bubble.description}</Typography>}
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            <Chip size="small" icon={<PeopleIcon />} label={`${bubble.member_count} members`} sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, bgcolor: isDarkMode ? '#2A2A2A' : '#F8F9FA' }} />
            {bubble.recent_activity > 0 && <Chip size="small" icon={<TrendingUpIcon />} label={`${bubble.recent_activity} posts`} color="primary" variant="outlined" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }} />}
            {bubble.has_chat && <Chip size="small" icon={<ChatIcon />} label="Chat" color="info" variant="outlined" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }} />}
          </Stack>
        </CardContent>
        <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 0, gap: 1 }}>
          {bubble.has_chat && bubble.conversation_id && (
            <Tooltip title="Open Chat" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenBubbleChat(bubble); }} sx={{ color: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}><ChatIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="View Community" arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewBubble(bubble.id); }} sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}><OpenInNewIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Box flex={1} />
          <Button variant="outlined" size="small" endIcon={<OpenInNewIcon />} onClick={() => handleViewBubble(bubble.id)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2 }}>View</Button>
        </CardActions>
      </Card>
    </Zoom>
  );

  const ModernActivityCard = ({ activity, index }) => {
    const isPost = activity.activity_type === "Post";
    const typeKey = activity.activity_type || activity.type;
    const colors = getProjectColors(typeKey, isDarkMode);
    return (
      <Zoom in timeout={300 + index * 100}>
        <Card elevation={0} sx={{
          borderRadius: 3,
          border: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`,
          backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer', position: 'relative', overflow: 'visible',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: isDarkMode ? '0 20px 40px rgba(0,0,0,0.3)' : '0 20px 40px rgba(0,0,0,0.1)', borderColor: colors.primary },
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${colors.primary}, ${colors.primary}80)`, borderRadius: '3px 3px 0 0' }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                <Avatar sx={{ width: 40, height: 40, backgroundColor: colors.bg, color: colors.primary }}>{iconForProject(typeKey)}</Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, color: isDarkMode ? '#FFFFFF' : '#1A1A1A', lineHeight: 1.3, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {activity.activity_name || activity.name || `Payout #${activity.id}`}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={typeKey === 'campaign' ? 'Campaign' : typeKey === 'partnership' ? 'Partnership' : typeKey} sx={{ backgroundColor: colors.bg, color: colors.primary, fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                    {activity.campaign_type && <Chip size="small" label={activity.campaign_type} sx={{ backgroundColor: colors.primary, color: '#FFFFFF', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />}
                  </Stack>
                </Box>
              </Box>
              <IconButton size="small" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'flex' } }}><MoreVertIcon fontSize="small" /></IconButton>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2} color="text.secondary">
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{new Date(activity.activity_date || activity.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
            </Box>
            {isPost && isAuthorized && (
              <CardActions sx={{ p: 0, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1}>
                  {['like', 'love', 'haha'].map((reactionType) => (
                    <Tooltip key={reactionType} title={`${reactionType} this post`}>
                      <IconButton size="small" onClick={() => handleReaction(activity.activity_id, reactionType)}
                        sx={{ color: activity.user_reaction === reactionType ? colors.primary : 'text.secondary', backgroundColor: activity.user_reaction === reactionType ? colors.bg : 'transparent', '&:hover': { backgroundColor: colors.hover, color: colors.primary }, transition: 'all 0.2s ease' }}>
                        {getReactionIcon(reactionType)}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Stack>
                {activity.total_reactions > 0 && <Typography variant="caption" color="text.secondary" fontWeight={600}>{activity.total_reactions} reaction{activity.total_reactions !== 1 ? 's' : ''}</Typography>}
              </CardActions>
            )}
          </CardContent>
        </Card>
      </Zoom>
    );
  };

  const AchievementCard = ({ bolt, index }) => {
    const attainment = bolt.attainment;
    return (
      <Zoom in timeout={300 + index * 100}>
        <Card elevation={0} sx={{
          borderRadius: 4, background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
          color: '#FFFFFF', position: 'relative', overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'translateY(-8px) scale(1.02)', boxShadow: '0 25px 50px rgba(102, 126, 234, 0.4)' },
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)', pointerEvents: 'none' }
        }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 }, position: 'relative', zIndex: 1 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              {bolt.icon_url ? (
                <Avatar src={bolt.icon_url} sx={{ width: { xs: 50, sm: 60 }, height: { xs: 50, sm: 60 }, border: '3px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }} />
              ) : (
                <Avatar sx={{ width: { xs: 50, sm: 60 }, height: { xs: 50, sm: 60 }, backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }}>
                  <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                </Avatar>
              )}
              <Box flex={1}>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.3)', mb: 0.5 }}>{bolt.title}</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <StarIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: '#FFD700' }} />
                  <Typography variant="body1" sx={{ fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.3)', fontSize: { xs: '0.9rem', sm: '1rem' } }}>{bolt.points || 0} points</Typography>
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6, opacity: 0.95, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>{bolt.description || "Achievement unlocked!"}</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  {attainment?.achieved_at ? new Date(attainment.achieved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Recent"}
                </Typography>
              </Box>
              <Tooltip title="Share achievement">
                <IconButton size="small" onClick={() => handleShareBlitzBolt(bolt)} sx={{ color: 'rgba(255,255,255,0.9)', backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)', transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
          <Box sx={{ position: 'absolute', top: 16, right: 16, backgroundColor: 'rgba(76, 175, 80, 0.9)', borderRadius: '50%', p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VerifiedIcon sx={{ fontSize: '1rem', color: '#FFFFFF' }} />
          </Box>
        </Card>
      </Zoom>
    );
  };

  const StatsCard = ({ icon, label, value, color }) => (
    <Paper elevation={0} sx={{
      p: { xs: 2, sm: 2.5 }, textAlign: 'center', borderRadius: 3,
      border: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`,
      backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
      transition: 'all 0.3s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: isDarkMode ? '0 10px 25px rgba(0,0,0,0.3)' : '0 10px 25px rgba(0,0,0,0.1)' }
    }}>
      <Avatar sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, backgroundColor: `${color}20`, color: color, mx: 'auto', mb: 1.5 }}>{icon}</Avatar>
      <Typography variant="h4" fontWeight={800} color={color} mb={0.5} sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{label}</Typography>
    </Paper>
  );

  /* ───── main render ───── */
  return (
    <>
      <CssBaseline />
      <PageTitle title={`${first} ${last} – Profile`} />
      <AlertDialog alertState={dialogState} />

      {/* Hero Section */}
      <Box sx={{
        position: 'relative', height: { xs: 250, sm: 350, md: 400 },
        background: `linear-gradient(135deg, ${isDarkMode ? '#1e3a8a' : '#3b82f6'} 0%, ${isDarkMode ? '#7c2d12' : '#ef4444'} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)' }
      }}>
        <Fade in timeout={1000}>
          <Avatar src={user.phphref || "/default-avatar.png"} sx={{ width: { xs: 100, sm: 140, md: 160 }, height: { xs: 100, sm: 140, md: 160 }, border: `6px solid ${isDarkMode ? '#1A1A1A' : '#FFFFFF'}`, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', zIndex: 2 }} />
        </Fade>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -6, sm: -8 }, position: 'relative', zIndex: 10, px: { xs: 2, sm: 3 } }}>
        {/* Profile Header */}
        <Fade in timeout={1200}>
          <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, sm: 4 }, mb: { xs: 3, sm: 4 }, border: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`, backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF', backdropFilter: 'blur(20px)' }}>
            <Box textAlign="center" mb={3}>
              <Typography variant="h3" fontWeight={800} sx={{ background: `linear-gradient(135deg, ${isDarkMode ? '#FFFFFF' : '#1A1A1A'} 0%, ${isDarkMode ? '#B0B0B0' : '#666666'} 100%)`, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
                {first} {last}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} justifyContent="center" alignItems="center" mb={3}>
                {company.location && <Chip icon={<PlaceIcon />} label={company.location} sx={{ backgroundColor: isDarkMode ? '#2A2A2A' : '#F8F9FA', color: isDarkMode ? '#FFFFFF' : '#1A1A1A', fontWeight: 600 }} />}
                {company.industry && <Chip icon={<WorkIcon />} label={company.industry} sx={{ backgroundColor: isDarkMode ? '#2A2A2A' : '#F8F9FA', color: isDarkMode ? '#FFFFFF' : '#1A1A1A', fontWeight: 600 }} />}
                {created_at && <Chip icon={<CalendarTodayIcon />} label={`Joined ${new Date(created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`} sx={{ backgroundColor: isDarkMode ? '#2A2A2A' : '#F8F9FA', color: isDarkMode ? '#FFFFFF' : '#1A1A1A', fontWeight: 600 }} />}
              </Stack>

              {/* Stats Overview */}
              <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                <Grid item xs={6} sm={3}><StatsCard icon={<PostAddIcon />} label="Posts" value={postsSorted.length} color="#6366F1" /></Grid>
                <Grid item xs={6} sm={3}><StatsCard icon={<BusinessCenterIcon />} label="Projects" value={totalProjects} color="#10B981" /></Grid>
                <Grid item xs={6} sm={3}><StatsCard icon={<GroupsIcon />} label="Bubbles" value={userBubbles.length} color="#F59E0B" /></Grid>
                <Grid item xs={6} sm={3}><StatsCard icon={<EmojiEventsIcon />} label="Achievements" value={achievedBlitzBolts.length} color="#8B5CF6" /></Grid>
              </Grid>

              {!isAuthorized && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2, backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.1)', border: `1px solid ${isDarkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`, '& .MuiAlert-icon': { color: isDarkMode ? '#64B5F6' : '#1976D2' } }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>Connect to Chat</AlertTitle>
                  Sign in to start a conversation with {first}
                </Alert>
              )}

              {/* ── Action Buttons ─────────────────────────────────────────── */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="center"
                alignItems="center"
                flexWrap="wrap"
              >
                {/* Message button */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isAuthorized ? <ChatIcon /> : <LockIcon />}
                  onClick={handleChatClick}
                  disabled={!isAuthorized}
                  sx={{
                    borderRadius: 3, px: 4, py: 1.5, fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    background: isAuthorized
                      ? `linear-gradient(135deg, ${isDarkMode ? '#667eea' : '#667eea'} 0%, ${isDarkMode ? '#764ba2' : '#764ba2'} 100%)`
                      : (isDarkMode ? '#424242' : '#E0E0E0'),
                    boxShadow: isAuthorized ? '0 8px 25px rgba(102, 126, 234, 0.4)' : 'none',
                    '&:hover': { transform: isAuthorized ? 'translateY(-2px)' : 'none', boxShadow: isAuthorized ? '0 12px 35px rgba(102, 126, 234, 0.5)' : 'none' },
                    '&:disabled': { color: isDarkMode ? '#757575' : '#BDBDBD' },
                  }}
                >
                  {isAuthorized ? `Message ${first}` : "Sign in to Chat"}
                </Button>

                {/* Single Transfer button — only when logged in and not own profile */}
                {isAuthorized && !isOwnUserProfile && (
                  <TransferDropdownButton
                    onGift={() => { setT2aMode("gift"); setT2aOpen(true); }}
                    onRequest={() => { setT2aMode("request"); setT2aOpen(true); }}
                  />
                )}
              </Stack>
            </Box>
          </Paper>
        </Fade>

        {/* Content Tabs */}
        <Paper ref={tabsRef} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`, backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF', overflow: 'hidden', scrollMarginTop: '20px' }}>
          <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{
            borderBottom: `1px solid ${isDarkMode ? '#2A2A2A' : '#F0F0F0'}`,
            "& .MuiTab-root": { color: isDarkMode ? '#B0B0B0' : '#666666', fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1.1rem' }, textTransform: 'none', py: { xs: 2, sm: 3 } },
            "& .Mui-selected": { color: isDarkMode ? '#667eea' : '#667eea' },
            "& .MuiTabs-indicator": { backgroundColor: '#667eea', height: 3, borderRadius: '3px 3px 0 0' }
          }}>
            <Tab label={<Box display="flex" alignItems="center" gap={1}><TrendingUpIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />{!isMobile && "Activity Feed"}{isMobile && "Feed"}</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><EmojiEventsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />{!isMobile && "Achievements"}{isMobile && "Awards"}</Box>} />
          </Tabs>

          {/* Unified Activity Tab */}
          {tab === 0 && (
            <Box p={{ xs: 2, sm: 4 }}>
              {(postsLoading || bubblesLoading) && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
              {unifiedFeed.length > 0 ? (
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  {unifiedFeed.map((item, index) => {
                    const key = item.activity_type === 'bubble' ? `bubble-${item.id}` : `activity-${item.activity_id || item.id || index}`;
                    if (item.activity_type === 'bubble') return <Grid item xs={12} sm={6} lg={4} key={key}><EnhancedBubbleCard bubble={item} index={index} /></Grid>;
                    return <Grid item xs={12} md={6} lg={4} key={key}><ModernActivityCard activity={item} index={index} /></Grid>;
                  })}
                </Grid>
              ) : (
                <Box textAlign="center" py={8} sx={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 3, border: `2px dashed ${isDarkMode ? '#404040' : '#E0E0E0'}` }}>
                  <TrendingUpIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: isDarkMode ? '#666666' : '#CCCCCC', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: isDarkMode ? '#B0B0B0' : '#666666', mb: 1, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>No activity yet</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? '#888888' : '#999999', fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>{first} hasn't shared any content or joined communities yet</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Achievements Tab */}
          {tab === 1 && (
            <Box p={{ xs: 2, sm: 4 }}>
              {!canViewAchievements && !isOwnUserProfile ? (
                <Box sx={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 3, border: `2px dashed ${isDarkMode ? '#404040' : '#E0E0E0'}`, p: { xs: 3, sm: 4 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}><LockIcon color="action" /><Typography variant="h6" sx={{ fontWeight: 700 }}>Achievements are private</Typography></Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>This user has chosen to keep BlitzBolt achievements private.</Typography>
                </Box>
              ) : (
                <>
                  {blitzBoltsLoading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
                  {achievedBlitzBolts.length > 0 ? (
                    <Grid container spacing={{ xs: 3, sm: 4 }}>
                      {achievedBlitzBolts.map((bolt, index) => <Grid key={`achievement-${bolt.id || index}`} item xs={12} sm={6} lg={4}><AchievementCard bolt={bolt} index={index} /></Grid>)}
                    </Grid>
                  ) : (
                    <Box textAlign="center" py={8} sx={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 3, border: `2px dashed ${isDarkMode ? '#404040' : '#E0E0E0'}` }}>
                      <EmojiEventsIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: isDarkMode ? '#666666' : '#CCCCCC', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: isDarkMode ? '#B0B0B0' : '#666666', mb: 1, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>No achievements yet</Typography>
                      <Typography variant="body2" sx={{ color: isDarkMode ? '#888888' : '#999999', fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>Complete activities to unlock BlitzBolts and showcase accomplishments</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Chat Modal */}
      {isAuthorized && (
        <>
          {isCreatorMode ? (
            <ChatModalCreator open={chatOpen} onClose={() => setChatOpen(false)} recipientUserId={userId} />
          ) : (
            <ChatModal open={chatOpen} onClose={() => setChatOpen(false)} recipientId={userId} />
          )}
        </>
      )}

      {/* T2A Universal Dialog */}
      <T2AUniversalDialog
        open={t2aOpen}
        onClose={() => setT2aOpen(false)}
        mode={t2aMode}
        recipientName={`${first} ${last}`.trim() || "them"}
        recipientGlobalUserId={null}
        resolveRecipientGUId={resolveRecipientGUId}
      />
    </>
  );
}